import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";

// Motifs selon qui annule : un client annule SA commande, un partenaire
// (restaurant/livreur) annule pour des raisons opérationnelles.
const REASONS_BY_ROLE = {
  client: [
    { v: "erreur_commande", k: "com.cErreur" },
    { v: "changement_avis", k: "com.cAvis" },
    { v: "delai_trop_long", k: "com.cDelai" },
    { v: "imprevu_livraison", k: "com.cImprevu" },
    { v: "autre", k: "com.cAutre" },
  ],
  restaurant: [
    { v: "rupture", k: "com.rRupture" },
    { v: "client_injoignable", k: "com.rInjoignable" },
    { v: "adresse_introuvable", k: "com.rAdresse" },
    { v: "autre", k: "com.cAutre" },
  ],
  admin: [
    { v: "rupture", k: "com.rRupture" },
    { v: "client_injoignable", k: "com.rInjoignable" },
    { v: "adresse_introuvable", k: "com.rAdresse" },
    { v: "erreur_commande", k: "com.aErreur" },
    { v: "imprevu_livraison", k: "com.aImprevu" },
    { v: "autre", k: "com.cAutre" },
  ],
};

/**
 * Props:
 *  - role: "client" | "restaurant" | "admin" (défaut "restaurant")
 *  - showCompensation: bool — admin uniquement, propose d'indemniser le restaurant
 */
export default function CancelOrderModal({ open, onClose, orderId, onCancelled, role = "restaurant", showCompensation = false }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [compensation, setCompensation] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const reasons = REASONS_BY_ROLE[role] || REASONS_BY_ROLE.restaurant;
  const commentRequired = reason === "autre";

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) return toast.error(t("com.chooseReason"));
    if (commentRequired && !comment.trim()) return toast.error(t("com.commentNeeded"));
    setBusy(true);
    try {
      const body = { reason, comment: comment.trim() };
      if (role === "admin" && showCompensation && Number(compensation) > 0) {
        body.compensate_restaurant_amount = Number(compensation);
      }
      const { data } = await apiClient.post(`/orders/${orderId}/cancel`, body);
      toast.success(t("com.cancelled"));
      onCancelled?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" data-testid="cancel-order-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-6 relative">
        <button type="button" onClick={onClose} data-testid="cancel-modal-close" className="absolute right-3 top-3 rounded-full p-1.5 hover:bg-elevated" aria-label={t("com.close")}>
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-brand-red" />
          <h3 className="font-display text-xl font-semibold">{t("com.title")}</h3>
        </div>
        <p className="text-sm text-mute mb-4">{t("com.hint")}</p>

        <div className="space-y-2 mb-4">
          {reasons.map((r) => (
            <label key={r.v} className="flex items-center gap-3 p-3 rounded-xl border border-sand hover:border-brand-red cursor-pointer" data-testid={`cancel-reason-${r.v}`}>
              <input
                type="radio"
                name="reason"
                value={r.v}
                checked={reason === r.v}
                onChange={() => setReason(r.v)}
                className="w-4 h-4"
              />
              <span className="text-sm">{t(r.k)}</span>
            </label>
          ))}
        </div>

        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-mute block mb-1">
            {t("com.comment")} {commentRequired ? <b className="text-brand-red">{t("com.required")}</b> : t("com.optional")}
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
            required={commentRequired}
            placeholder={commentRequired ? t("com.commentPh") : ""}
            data-testid="cancel-comment"
            className="w-full bg-white border border-sand rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-red"
          />
        </label>

        {role === "admin" && showCompensation && (
          <label className="block mb-4 rounded-xl border border-brand-yellow bg-brand-yellow/10 p-3" data-testid="cancel-compensation-block">
            <span className="text-xs uppercase tracking-widest text-[#8A6D00] font-bold block mb-1">{t("com.compTitle")}</span>
            <span className="text-xs text-mute block mb-2">{t("com.compHint")}</span>
            <div className="flex items-center gap-2">
              <input
                value={compensation}
                onChange={(e) => setCompensation(e.target.value.replace(/[^0-9]/g, ""))}
                inputMode="numeric"
                placeholder="0"
                data-testid="cancel-compensation-amount"
                className="flex-1 bg-white border border-sand rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-red text-right"
              />
              <span className="text-xs text-mute">GNF</span>
            </div>
          </label>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-full border border-sand hover:border-ink py-2.5 text-sm font-semibold" data-testid="cancel-modal-abort">
            {t("com.back")}
          </button>
          <button
            type="submit"
            disabled={busy || !reason}
            data-testid="cancel-modal-confirm"
            className="flex-1 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("com.confirm")}
          </button>
        </div>
      </form>
    </div>
  );
}
